import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, TruckIcon, CalendarIcon, PackageIcon, InfoIcon, AlertCircleIcon, MapPinIcon, BuildingIcon, ClipboardIcon } from 'lucide-react';
interface Address {
  name: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  contactName: string;
  contactPhone: string;
}
interface Stop {
  type: 'Pickup' | 'Delivery';
  address: string;
  city: string;
  state: string;
  zip: string;
  contactName: string;
  contactPhone: string;
  appointmentEarliest: string;
  appointmentLatest: string;
  pickupNumber: string;
  remarks: string;
}
interface Carrier {
  id: string;
  name: string;
  type: 'contracted' | 'RR';
}
const CreateShipment = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showSpecialRequirements, setShowSpecialRequirements] = useState(false);
  const [recentAddresses, setRecentAddresses] = useState<Address[]>([]);
  const [carriers, setCarriers] = useState<Carrier[]>([]);
  const [equipmentTypes, setEquipmentTypes] = useState<string[]>([]);
  const [commodities, setCommodities] = useState<string[]>([]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  // Form state
  const [shipmentData, setShipmentData] = useState({
    orderNumber: '',
    customer: '',
    carrier: '',
    equipmentType: '',
    stops: [] as Stop[],
    totalWeight: '',
    palletCount: '',
    commodity: '',
    notes: '',
    hazardousMaterials: false,
    temperatureControlled: false,
    temperatureRange: '',
    specialRequirements: {
      liftGatePickup: false,
      liftGateDelivery: false,
      insidePickup: false,
      insideDelivery: false,
      residentialPickup: false,
      residentialDelivery: false
    }
  });
  // Mock data loading
  useEffect(() => {
    // Simulate loading carrier data
    setCarriers([{
      id: '1',
      name: 'Carrier A',
      type: 'contracted'
    }, {
      id: '2',
      name: 'Carrier B',
      type: 'contracted'
    }, {
      id: '3',
      name: 'R&R Carrier 1',
      type: 'RR'
    }]);
    // Simulate loading equipment types
    setEquipmentTypes(['Dry Van', 'Refrigerated', 'Flatbed', 'Step Deck', 'Double Drop']);
    // Simulate loading commodities
    setCommodities(['General Freight', 'Food Products', 'Building Materials', 'Machinery', 'Automotive']);
    // Simulate loading recent addresses
    setRecentAddresses([{
      name: 'Main Warehouse',
      address: '123 Warehouse Ave',
      city: 'Chicago',
      state: 'IL',
      zip: '60601',
      contactName: 'John Smith',
      contactPhone: '(312) 555-0123'
    }
    // Add more recent addresses...
    ]);
  }, []);
  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    // Order number validation
    if (!shipmentData.orderNumber) {
      newErrors.orderNumber = 'Order number is required';
    } else if (shipmentData.orderNumber.length > 100) {
      newErrors.orderNumber = 'Order number cannot exceed 100 characters';
    }
    // Equipment type validation
    if (!shipmentData.equipmentType) {
      newErrors.equipmentType = 'Equipment type is required';
    }
    // Stops validation
    if (!shipmentData.stops.some(stop => stop.type === 'Pickup')) {
      newErrors.stops = 'At least one pickup stop is required';
    }
    if (!shipmentData.stops.some(stop => stop.type === 'Delivery')) {
      newErrors.stops = 'At least one delivery stop is required';
    }
    // Notes validation
    if (shipmentData.notes.length > 1000) {
      newErrors.notes = 'Order notes cannot exceed 1000 characters';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) {
      return;
    }
    setLoading(true);
    // Simulate API call
    setTimeout(() => {
      setLoading(false);
      navigate('/shipments');
    }, 1500);
  };
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const {
      name,
      value,
      type
    } = e.target;
    const isCheckbox = type === 'checkbox';
    setShipmentData(prev => ({
      ...prev,
      [name]: isCheckbox ? (e.target as HTMLInputElement).checked : value
    }));
  };
  const addStop = (type: 'Pickup' | 'Delivery') => {
    setShipmentData(prev => ({
      ...prev,
      stops: [...prev.stops, {
        type,
        address: '',
        city: '',
        state: '',
        zip: '',
        contactName: '',
        contactPhone: '',
        appointmentEarliest: '',
        appointmentLatest: '',
        pickupNumber: '',
        remarks: ''
      }]
    }));
  };
  const updateStop = (index: number, data: Partial<Stop>) => {
    setShipmentData(prev => ({
      ...prev,
      stops: prev.stops.map((stop, i) => i === index ? {
        ...stop,
        ...data
      } : stop)
    }));
  };
  const removeStop = (index: number) => {
    setShipmentData(prev => ({
      ...prev,
      stops: prev.stops.filter((_, i) => i !== index)
    }));
  };
  return <div className="py-3" data-id="element-454">
      <div className="mb-3" data-id="element-455">
        <button onClick={() => navigate(-1)} className="flex items-center text-gray-600 hover:text-gray-900 text-sm" data-id="element-456">
          <ArrowLeftIcon size={14} className="mr-1" data-id="element-457" /> Back
        </button>
        <h1 className="text-xl font-semibold text-gray-800 mt-1" data-id="element-458">
          Create New Load
        </h1>
      </div>
      <div className="bg-white rounded-md shadow-sm" data-id="element-459">
        <form onSubmit={handleSubmit} data-id="element-460">
          <div className="p-4 space-y-4" data-id="element-461">
            {/* Basic Information - Compact Layout */}
            <div className="border-b border-gray-200 pb-4" data-id="element-462">
              <h2 className="text-md font-medium text-gray-900 mb-3 flex items-center" data-id="element-463">
                <ClipboardIcon size={18} className="mr-2 text-primary-700" data-id="element-464" />
                Basic Information
              </h2>
              {/* Row 1: Order Number, Customer, Equipment Type */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-3" data-id="element-465">
                <div data-id="element-466">
                  <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-467">
                    Order Number *
                  </label>
                  <input type="text" name="orderNumber" value={shipmentData.orderNumber} onChange={handleInputChange} maxLength={100} className={`w-full px-2 py-1.5 text-sm border ${errors.orderNumber ? 'border-red-500' : 'border-gray-300'} rounded focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500`} required tabIndex={1} data-id="element-468" />
                  {errors.orderNumber && <p className="mt-0.5 text-xs text-red-600" data-id="element-469">
                      {errors.orderNumber}
                    </p>}
                </div>
                <div data-id="element-470">
                  <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-471">
                    Customer *
                  </label>
                  <select name="customer" value={shipmentData.customer} onChange={handleInputChange} className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500" required tabIndex={2} data-id="element-472">
                    <option value="" data-id="element-473">Select Customer</option>
                    {/* Add customer options */}
                  </select>
                </div>
                <div data-id="element-474">
                  <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-475">
                    Equipment Type *
                  </label>
                  <select name="equipmentType" value={shipmentData.equipmentType} onChange={handleInputChange} className={`w-full px-2 py-1.5 text-sm border ${errors.equipmentType ? 'border-red-500' : 'border-gray-300'} rounded focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500`} required tabIndex={3} data-id="element-476">
                    <option value="" data-id="element-477">Select Equipment Type</option>
                    {equipmentTypes.map(type => <option key={type} value={type} data-id="element-478">
                        {type}
                      </option>)}
                  </select>
                  {errors.equipmentType && <p className="mt-0.5 text-xs text-red-600" data-id="element-479">
                      {errors.equipmentType}
                    </p>}
                </div>
              </div>
              {/* Row 2: Carrier, Weight, Pallets, Commodity */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3" data-id="element-480">
                <div data-id="element-481">
                  <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-482">
                    Carrier
                  </label>
                  <select name="carrier" value={shipmentData.carrier} onChange={handleInputChange} className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500" tabIndex={4} data-id="element-483">
                    <option value="" data-id="element-484">Select Carrier</option>
                    {carriers.map(carrier => <option key={carrier.id} value={carrier.id} data-id="element-485">
                        {carrier.name} (
                        {carrier.type === 'contracted' ? 'Contracted' : 'R&R'})
                      </option>)}
                  </select>
                </div>
                <div data-id="element-486">
                  <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-487">
                    Weight (lbs) *
                  </label>
                  <input type="number" name="totalWeight" value={shipmentData.totalWeight} onChange={handleInputChange} className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500" required tabIndex={5} data-id="element-488" />
                </div>
                <div data-id="element-489">
                  <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-490">
                    Pallets
                  </label>
                  <input type="number" name="palletCount" value={shipmentData.palletCount} onChange={handleInputChange} className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500" tabIndex={6} data-id="element-491" />
                </div>
                <div data-id="element-492">
                  <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-493">
                    Commodity
                  </label>
                  <select name="commodity" value={shipmentData.commodity} onChange={handleInputChange} className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500" tabIndex={7} data-id="element-494">
                    <option value="" data-id="element-495">Select Commodity</option>
                    {commodities.map(commodity => <option key={commodity} value={commodity} data-id="element-496">
                        {commodity}
                      </option>)}
                  </select>
                </div>
              </div>
            </div>
            {/* Stops Section - Compact */}
            <div className="border-b border-gray-200 pb-4" data-id="element-497">
              <div className="flex justify-between items-center mb-3" data-id="element-498">
                <h2 className="text-md font-medium text-gray-900 flex items-center" data-id="element-499">
                  <MapPinIcon size={18} className="mr-2 text-primary-700" data-id="element-500" />
                  Stops
                </h2>
                <div className="space-x-2" data-id="element-501">
                  <button type="button" onClick={() => addStop('Pickup')} className="px-2 py-1 border border-gray-300 rounded text-xs font-medium text-gray-700 hover:bg-gray-50" data-id="element-502">
                    Add Pickup
                  </button>
                  <button type="button" onClick={() => addStop('Delivery')} className="px-2 py-1 border border-gray-300 rounded text-xs font-medium text-gray-700 hover:bg-gray-50" data-id="element-503">
                    Add Delivery
                  </button>
                </div>
              </div>
              {errors.stops && <div className="mb-3 p-2 bg-red-50 border-l-4 border-red-500 text-red-700 text-sm" data-id="element-504">
                  {errors.stops}
                </div>}
              {shipmentData.stops.map((stop, index) => <div key={index} className="mb-3 p-3 bg-gray-50 rounded border" data-id="element-505">
                  <div className="flex justify-between items-center mb-2" data-id="element-506">
                    <h3 className="text-sm font-medium text-gray-900" data-id="element-507">
                      {stop.type} #{index + 1}
                    </h3>
                    <button type="button" onClick={() => removeStop(index)} className="text-red-600 hover:text-red-800 text-xs" data-id="element-508">
                      Remove
                    </button>
                  </div>
                  {/* Address Selection */}
                  <div className="mb-2" data-id="element-509">
                    <select onChange={e => {
                  const selectedAddress = recentAddresses.find(addr => addr.address === e.target.value);
                  if (selectedAddress) {
                    updateStop(index, selectedAddress);
                  }
                }} className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500 mb-1" data-id="element-510">
                      <option value="" data-id="element-511">Select from recent addresses</option>
                      {recentAddresses.map((addr, i) => <option key={i} value={addr.address} data-id="element-512">
                          {addr.name} - {addr.address}
                        </option>)}
                    </select>
                  </div>
                  {/* Address Fields - Compact Grid */}
                  <div className="grid grid-cols-1 md:grid-cols-6 gap-2 mb-2" data-id="element-513">
                    <div className="md:col-span-2" data-id="element-514">
                      <input type="text" value={stop.address} onChange={e => updateStop(index, {
                    address: e.target.value
                  })} placeholder="Street Address" className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500" data-id="element-515" />
                    </div>
                    <div data-id="element-516">
                      <input type="text" value={stop.city} onChange={e => updateStop(index, {
                    city: e.target.value
                  })} placeholder="City" className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500" data-id="element-517" />
                    </div>
                    <div data-id="element-518">
                      <select value={stop.state} onChange={e => updateStop(index, {
                    state: e.target.value
                  })} className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500" required data-id="element-519">
                        <option value="" data-id="element-520">State</option>
                        {/* Add state options */}
                      </select>
                    </div>
                    <div data-id="element-521">
                      <input type="text" value={stop.zip} onChange={e => updateStop(index, {
                    zip: e.target.value
                  })} placeholder="ZIP" className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500" required data-id="element-522" />
                    </div>
                    <div data-id="element-523">
                      <input type="text" value={stop.pickupNumber} onChange={e => updateStop(index, {
                    pickupNumber: e.target.value
                  })} placeholder="PU/DEL #" maxLength={100} className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500" data-id="element-524" />
                    </div>
                  </div>
                  {/* Contact and Time Fields */}
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-2" data-id="element-525">
                    <div data-id="element-526">
                      <input type="text" value={stop.contactName} onChange={e => updateStop(index, {
                    contactName: e.target.value
                  })} placeholder="Contact Name" className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500" data-id="element-527" />
                    </div>
                    <div data-id="element-528">
                      <input type="tel" value={stop.contactPhone} onChange={e => updateStop(index, {
                    contactPhone: e.target.value
                  })} placeholder="Contact Phone" className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500" data-id="element-529" />
                    </div>
                    <div data-id="element-530">
                      <input type="datetime-local" value={stop.appointmentEarliest} onChange={e => updateStop(index, {
                    appointmentEarliest: e.target.value
                  })} className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500" title="Earliest Appointment" data-id="element-531" />
                    </div>
                    <div data-id="element-532">
                      <input type="datetime-local" value={stop.appointmentLatest} onChange={e => updateStop(index, {
                    appointmentLatest: e.target.value
                  })} className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500" title="Latest Appointment" data-id="element-533" />
                    </div>
                  </div>
                  {/* Remarks */}
                  {stop.remarks !== undefined && <div className="mt-2" data-id="element-534">
                      <textarea value={stop.remarks} onChange={e => updateStop(index, {
                  remarks: e.target.value
                })} placeholder="Remarks" rows={1} className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500" data-id="element-535" />
                    </div>}
                </div>)}
            </div>
            {/* Notes Section - Compact */}
            <div data-id="element-536">
              <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-537">
                Order Notes
              </label>
              <textarea name="notes" value={shipmentData.notes} onChange={handleInputChange} rows={2} maxLength={1000} className={`w-full px-2 py-1.5 text-sm border ${errors.notes ? 'border-red-500' : 'border-gray-300'} rounded focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500`} placeholder="Enter any special instructions or requirements..." data-id="element-538" />
              {errors.notes && <p className="mt-0.5 text-xs text-red-600" data-id="element-539">{errors.notes}</p>}
              <p className="mt-0.5 text-xs text-gray-500" data-id="element-540">
                {shipmentData.notes.length}/1000 characters
              </p>
            </div>
          </div>
          {/* Form Actions */}
          <div className="px-4 py-2 bg-gray-50 text-right rounded-b-md border-t border-gray-200" data-id="element-541">
            <button type="submit" disabled={loading} className="px-3 py-1.5 border border-transparent rounded text-sm font-medium text-white bg-primary-700 hover:bg-primary-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:bg-primary-300 inline-flex items-center" data-id="element-542">
              {loading ? <>
                  <svg className="animate-spin -ml-1 mr-2 h-3 w-3 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" data-id="element-543">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-544"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" data-id="element-545"></path>
                  </svg>
                  Processing...
                </> : 'Create Load'}
            </button>
          </div>
        </form>
      </div>
    </div>;
};
export default CreateShipment;